import { Injectable } from '@angular/core';
import { AceLoggerService } from '@logging/ace-logger.service';
import { AceLogger } from '@logging/ace-logger';


interface Colours {
  colours: string[];
  highVisibilityColours: string[];
  iteration: number;
}


export interface ColourAccessors {
  getColour: (index: number) => string;
  getHighVisibilityColour: (index: number) => string;
}


export interface ColourGenerationOptions {
  highVisibility?: boolean;
  iteration?: number;
}


interface HSV {
  hue: number;
  saturation: number;
  value: number;
}


enum HsvVariable {
  hue,
  saturation,
  value
}


enum SpiralDirection {
  IN = 'IN',
  OUT = 'OUT',
  UP = 'UP',
  DOWN = 'DOWN'
}


@Injectable({
  providedIn: 'root'
})
export class ColourGenerationService {
  private readonly logger: AceLogger;

  private readonly DEFAULT_COLOUR_PROFILE = 'cim';
  private readonly DEFAULT_PRIMARY_COLOUR = '#4661EE';
  private readonly DEFAULT_HIGH_VIS_PRIMARY_COLOUR = '#00CCFF';

  private readonly VALUE_LOWER_BOUND = 0.3;  // lower than 0.3, colour becomes too dark to be usable
  private readonly VALUE_UPPER_BOUND = 0.8;  // blocking out 0.8 - 1 for high vis colours
  private readonly VALUE_STEP = 0.1;

  private readonly SATURATION_LOWER_BOUND = 0.3;  // lower than 0.3 colour becomes too white to be usable
  private readonly SATURATION_UPPER_BOUND = 0.8;  // blocking out 0.8 - 1 for high vis colours
  private readonly SATURATION_STEP = 0.1;

  private readonly HSV_CONE_SECTIONS = 18;  // split 360 degrees into 18 sections
  private readonly HSV_SECTION_SPAN = 360 / this.HSV_CONE_SECTIONS / 180;  // normalise section span to range of [0, 1]
  private readonly HUE_STEP = this.HSV_SECTION_SPAN * 13;  // to achieve ok colour difference, rotate next colour by 13 sections
                                                           // it's important to have a number that can exhaust all 18 sections
                                                           // after rotation

  private generatedColours: Set<string> = new Set(); // keep track of generated colours to avoid colour clash
  private profileMap: Map<string, Colours> = new Map();

  constructor(logService: AceLoggerService) {
    this.logger = logService.create(ColourGenerationService.name);
  }

  private getSaturationStep(iteration: number = 1): number {
    if (iteration <= 0) {
      throw new Error('Invalid iteration ' + iteration);
    }
    return this.SATURATION_STEP / iteration;
  }

  private getValueStep(iteration: number = 1): number {
    if (iteration <= 0) {
      throw new Error('Invalid iteration ' + iteration);
    }
    return this.VALUE_STEP / iteration;
  }

  private getSaturationLowerBound(iteration: number = 1): number {
    if (iteration <= 0) {
      throw new Error('Invalid iteration ' + iteration);
    }
    return this.SATURATION_LOWER_BOUND / iteration;
  }

  private getValueLowerBound(iteration: number = 1): number {
    if (iteration <= 0) {
      throw new Error('Invalid iteration ' + iteration);
    }
    return this.VALUE_LOWER_BOUND / iteration;
  }

  private nextHsv(hsv: HSV, distance: number, variable: HsvVariable): HSV {
    let { hue, saturation, value } = hsv;

    const hueSaturationAdjustment = this.HUE_STEP * (1 - saturation);
    const hueValueAdjustment = this.HUE_STEP * (1 - value);

    switch (variable) {
      case HsvVariable.hue:
        hue = ((hue + (distance + hueSaturationAdjustment + hueValueAdjustment) * 180) + 360) % 360;
        break;
      case HsvVariable.saturation:
        saturation = Math.max(saturation - distance, 0);
        break;
      case HsvVariable.value:
        value = Math.max(value - distance, 0);
        break;
    }
    return {
      hue,
      saturation,
      value,
    };
  }

  private hexToHsv(hex: string): HSV {
    const [, r, g, b] = hex.match(/#?(..)(..)(..)/);
    const rValue = parseInt(r, 16) / 255;
    const gValue = parseInt(g, 16) / 255;
    const bValue = parseInt(b, 16) / 255;

    const cMax = Math.max(rValue, gValue, bValue);
    const cMin = Math.min(rValue, gValue, bValue);
    const range = cMax - cMin;

    let hue, saturation;
    if (range === 0) {
      hue = 0;
    } else if (cMax === rValue) {
      hue = 60 * (((gValue - bValue) / range) % 6);
    } else if (cMax === gValue) {
      hue = 60 * (2 + (bValue - rValue) / range);
    } else {
      // cMax === bValue
      hue = 60 * (4 + (rValue - gValue) / range);
    }

    if (cMax === 0) {
      saturation = 0;
    } else {
      saturation = range / cMax;
    }

    return { hue: (hue + 360) % 360, saturation, value: cMax };
  }

  private decToHex(dec: number) {
    const base16String: string = (Math.round(dec) + Math.pow(16, 2)).toString(16);
    return base16String.substring(base16String.length -2);
  }

  private hsvToHex(hsv: HSV): string {
    const { hue, saturation, value } = hsv;
    const c = value * saturation;
    const x = (1 - Math.abs((hue / 60) % 2 - 1)) * c;
    const m = value - c;

    let rd: number, gd: number, bd: number;
    if (hue >= 0 && hue < 60) {
      [rd, gd, bd] = [c, x, 0];
    } else if (hue >= 60 && hue < 120) {
      [rd, gd, bd] = [x, c, 0];
    } else if (hue >= 120 && hue < 180) {
      [rd, gd, bd] = [0, c, x];
    } else if (hue >= 180 && hue < 240) {
      [rd, gd, bd] = [0, x, c];
    } else if (hue >= 240 && hue < 300) {
      [rd, gd, bd] = [x, 0, c];
    } else if (hue >= 300 && hue < 360) {
      [rd, gd, bd] = [c, 0, x];
    } else {
      this.logger.error('Invalid hsv: ', hsv, ', cannot convert hsv to hex.');
      return null;
    }

    const [rv, gv, bv] = [Math.round(255 * (rd + m)), Math.round(255 * (gd + m)), Math.round(255 * (bd + m))];
    return '' + this.decToHex(rv) + this.decToHex(gv) + this.decToHex(bv);
  }

  /**
   * Get the sign for steps based on given spiral direction.
   * Value and Saturation range from [0, 1]
   * when value = 0, colour is indistinguishable from black.
   * when saturation = 0, colour is indistinguishable from white.
   *
   * In our code we use hsv cone as model:
   *   when spiraling up, value grows from 0 to 1
   *   when spiraling out, saturation grows from 0 to 1
   */
  private getSpiralStepSign(direction: SpiralDirection): 1 | -1 {
    switch (direction) {
      case SpiralDirection.UP:
      case SpiralDirection.OUT:
        return -1;
      case SpiralDirection.DOWN:
      case SpiralDirection.IN:
        return 1;
      default:
        throw new Error('Invalid Spiral Direction: ' + direction);
    }
  }

  // spin the colour wheel and get the next colour (difference in hue) according to hsv and direction
  private spiral(hsv: HSV, direction: SpiralDirection): { endHsv: HSV, colours: string[] } {
    let newHsv = hsv;
    const colours = [];
    const hueStep = this.getSpiralStepSign(direction) * this.HUE_STEP;
    for (let i = 0; i < this.HSV_CONE_SECTIONS; i++) {
      const hex = this.hsvToHex(newHsv);
      if (!this.generatedColours.has(hex)) {
        colours.push('#' + hex);
        this.generatedColours.add(hex);
      }
      newHsv = this.nextHsv(newHsv, hueStep, HsvVariable.hue);
    }

    return { endHsv: newHsv, colours };
  }

  // spin the colour wheel, pick the colours with same saturation and higher saturation
  // each saturation change will result in 18 colours as per defined by HSV_CONE_SECTIONS
  private spiralOut(hsv: HSV, options?: ColourGenerationOptions): string[] { // saturation increases to 1
    const { highVisibility, iteration } = options || <ColourGenerationOptions>{};
    const upperbound = highVisibility ? 1 : this.SATURATION_UPPER_BOUND;
    const lowerbound = this.getSaturationLowerBound(iteration);
    if (hsv.saturation > upperbound || hsv.saturation < lowerbound) {
      return [];
    }
    let newHsv = hsv;
    const result = [];
    while (newHsv.saturation <= upperbound) {
      const { endHsv, colours } = this.spiral(newHsv, SpiralDirection.OUT);
      result.push(...colours);
      newHsv = this.nextHsv(endHsv, -this.getSaturationStep(iteration), HsvVariable.saturation);
      // rotate the hue wheel to get the next hue, as the start of next round of spiral
      newHsv = this.nextHsv(newHsv, this.HUE_STEP, HsvVariable.hue);
    }
    return result;
  }

  // same as spiralOut, but colours will have same and lower saturation
  private spiralIn(hsv: HSV, options?: ColourGenerationOptions): string[] { // saturation reduces
    const { highVisibility, iteration } = options || <ColourGenerationOptions>{};
    const upperbound = highVisibility ? 1 : this.SATURATION_UPPER_BOUND;
    const lowerbound = highVisibility ? this.SATURATION_UPPER_BOUND : this.getSaturationLowerBound(iteration);

    if (hsv.saturation > upperbound || hsv.saturation < lowerbound) {
      return [];
    }
    const result = [];
    let newHsv = hsv;

    while (newHsv.saturation >= lowerbound) {
      const { endHsv, colours } = this.spiral(newHsv, SpiralDirection.IN);
      result.push(...colours);
      newHsv = this.nextHsv(endHsv, this.getSaturationStep(iteration), HsvVariable.saturation);
      // rotate the hue wheel to get the next hue, as the start of next round of spiral
      newHsv = this.nextHsv(newHsv, this.HUE_STEP, HsvVariable.hue);
    }
    return result;
  }

  // move picker along hsv colour cone upwards, pick the colours with same value and higher value
  // each value change will result in colours generated in spiralOut and spiralIn.
  private spiralUp(hsv: HSV, options?: ColourGenerationOptions): string[] {
    const { highVisibility, iteration } = options || <ColourGenerationOptions>{};
    const upperbound = highVisibility ? 1 : this.VALUE_UPPER_BOUND;
    const lowerbound = this.getValueLowerBound(iteration);
    if (hsv.value > upperbound || hsv.value < lowerbound) {
      return [];
    }
    const result = [];
    let newHsv = hsv;

    while (newHsv.value <= upperbound) {
      result.push(...this.spiralOut(newHsv, options));
      result.push(...this.spiralIn(newHsv, options));
      newHsv = this.nextHsv(newHsv, -this.getValueStep(iteration), HsvVariable.value);
      // rotate the hue wheel to get the next hue, as the start of next round of spiral
      newHsv = this.nextHsv(newHsv, this.HUE_STEP, HsvVariable.hue);
    }
    return result;
  }

  // move picker along hsv colour cone downwards, pick the colours with same value and higher value
  // each value change will result in colours generated in spiralOut and spiralIn.
  private spiralDown(hsv: HSV, options?: ColourGenerationOptions): string[] {
    const { highVisibility, iteration } = options || <ColourGenerationOptions>{};
    const upperbound = highVisibility ? 1 : this.VALUE_UPPER_BOUND;
    const lowerbound = highVisibility ? this.VALUE_UPPER_BOUND : this.getValueLowerBound(iteration);

    if (hsv.value > upperbound || hsv.value < lowerbound) {
      return [];
    }
    const result = [];
    let newHsv = hsv;

    while (newHsv.value >= lowerbound) {
      result.push(...this.spiralOut(newHsv, options));
      result.push(...this.spiralIn(newHsv, options));
      newHsv = this.nextHsv(newHsv, this.getValueStep(iteration), HsvVariable.value);
      // rotate the hue wheel to get the next hue, as the start of next round of spiral
      newHsv = this.nextHsv(newHsv, this.HUE_STEP, HsvVariable.hue);
    }
    return result;
  }

  private generateColours(startColourHex: string, options?: ColourGenerationOptions): string[] {
    this.generatedColours.clear();
    let hsv = this.hexToHsv(startColourHex);
    const { highVisibility, iteration } = options || <ColourGenerationOptions>{ iteration: 1 };

    if (iteration === 1) { // only do adjustment when it's the first time generating colours, later iterations will be rogue colours
      // bring the color to its respective saturation and value, avoid colour clash.
      if (highVisibility) {
        if (hsv.saturation < this.SATURATION_UPPER_BOUND) {
          hsv.saturation = 1;
        }
        if (hsv.value < this.VALUE_UPPER_BOUND) {
          hsv.value = 1;
        }
      } else {
        // same applies here, if s/v is in high visibility range, bring them out to the edge of non-high visibility.
        if (hsv.saturation > this.SATURATION_UPPER_BOUND) {
          hsv.saturation = this.SATURATION_UPPER_BOUND - this.SATURATION_STEP;
        }
        if (hsv.value > this.VALUE_UPPER_BOUND) {
          hsv.value = this.VALUE_UPPER_BOUND - this.VALUE_STEP;
        }
      }
    }

    if (iteration > 1) {
      // when expanding(iteration > 1), sink the colour down by one value step and start expanding
      hsv = this.nextHsv(hsv, this.getValueStep(iteration), HsvVariable.value);
    }

    const result = [];

    if (iteration === 1) {
      // expanding(iteration > 1) will head towards the direction where colours are harder to differentiate from each other, hence should not be spiraling up
      result.push(...this.spiralUp(hsv, options));
    }
    result.push(...this.spiralDown(hsv, options));

    return result;
  }

  /**
   * Get colours by assigned profile name, if colours didn't exist for profile, generate and then return.
   *
   * @param profile name for the colour profile
   * @param primaryColour primary colour for profile
   * @param primaryHighVisColour primary high visibility colour for profile
   */
  public getColoursByProfile(profile: string, primaryColour?: string, primaryHighVisColour?: string): ColourAccessors {
    if (!this.profileMap.has(profile)) {
      if (primaryColour == null || primaryHighVisColour == null) {
        throw new Error(`Profile ${profile} does not have existing colours, please supply primary colour and primary high visibility colour.`);
      }
      this.profileMap.set(profile, {
        colours: this.generateColours(primaryColour),
        highVisibilityColours: this.generateColours(primaryHighVisColour, { highVisibility: true }),
        iteration: 1
      });
    }
    return {
      getColour: this.getColourAccessor(profile),
      getHighVisibilityColour: this.getColourAccessor(profile, true)
    };
  }

  public getDefaultProfileColourAccessors(): ColourAccessors {
    return this.getColoursByProfile(this.DEFAULT_COLOUR_PROFILE, this.DEFAULT_PRIMARY_COLOUR, this.DEFAULT_HIGH_VIS_PRIMARY_COLOUR);
  }

  private getColourAccessor(profile: string, highVisibility?: boolean) {
    const profileColours = this.profileMap.get(profile);
    const colours = highVisibility ? profileColours.highVisibilityColours : profileColours.colours;
    return (index: number) => {
      if (index >= colours.length) {
        this.expandColoursForProfile(profile);
      }
      return colours[index];
    };
  }

  /**
   * Expand colours for given profile, throw error if colours for profile had not been generated.
   *
   * @param profile name for the colour profile
   */
  private expandColoursForProfile(profile: string): Colours {
    // discussion, almost feel like we should just loop over the old colours instead of expanding...

    if (!this.profileMap.has(profile)) {
      throw new Error(`Profile ${profile} does not have existing colours, please generate the colours first.`);
    }
    const profileColours = this.profileMap.get(profile);
    const { colours, highVisibilityColours, iteration } = profileColours;
    const expandColourOptions: ColourGenerationOptions = { iteration: iteration + 1 };

    // expand normal colours
    colours.push(
        ...this.generateColours(
            colours[colours.length - 1],
            expandColourOptions));

    // expand high vis colours
    // high vis colours have already been exhausted in the first time around
    // further expansion is just looping over old colours
    highVisibilityColours.push(...highVisibilityColours.slice(0, highVisibilityColours.length / iteration));

    // increase iteration
    profileColours.iteration += 1;

    return profileColours;
  }
}
